"use client";

import React from "react";
import { ArrowRight, Phone, CalendarCheck, ShieldCheck, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function StartPlanning() {
  const shouldReduceMotion = useReducedMotion();
  const trustHighlights = [{
    id: 1,
    title: "Detailed cost guidance",
    subtitle: "Transparent estimates upfront",
    icon: CalendarCheck
  }, {
    id: 2,
    title: "Local Decatur crew",
    subtitle: "Rooted in our community",
    icon: Sparkles
  }, {
    id: 3,
    title: "Dedicated craftsmanship",
    subtitle: "Careful attention to structural detail",
    icon: ShieldCheck
  }];
  return <section id="start-planning-your-remodel" data-nav="light" className="bg-background text-foreground relative overflow-hidden py-16 sm:py-20 lg:py-24">
      {/* Subtle architectural grid lines decoration */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" aria-hidden="true">
        <div className="h-full w-full bg-[radial-gradient(#000000_1px,transparent_1px)] [background-size:24px_24px]" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto max-w-4xl">
          <Card className="relative overflow-hidden border-border bg-card/85 backdrop-blur-md shadow-lg py-8 sm:py-12 px-2 sm:px-6">
            {/* Architectural Copper Top Hairline Accent */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-chart-1" />

            <CardContent className="flex flex-col items-center text-center px-4 sm:px-8">
              {/* Eyebrow Tag */}
              <div className="mb-4">
                <Badge variant="outline" className="border-chart-1/30 bg-secondary/80 text-foreground font-sans px-3.5 py-1 text-xs font-semibold tracking-wider uppercase rounded-full">
                  Next Steps
                </Badge>
              </div>

              {/* Architectural Headline */}
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground max-w-2xl leading-tight mb-4">
                Start your remodeling roadmap
              </h2>

              {/* Supporting Paragraph */}
              <p className="font-sans text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed mb-8">
                Plan your project with clear timelines, transparent cost estimates, and Decatur craftsmen dedicated to lasting quality for your home.
              </p>

              {/* CTA Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5 w-full sm:w-auto mb-10">
                <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground font-sans font-semibold px-7 py-6 rounded-md shadow-sm hover:bg-primary/90 transition-colors">
                  <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                    <span>Request an estimate</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border bg-background text-foreground font-sans font-semibold px-6 py-6 rounded-md hover:bg-secondary/70 transition-colors">
                  <Link to="/ProjectsPage" className="inline-flex items-center justify-center gap-2">
                    <span>Explore recent work</span>
                  </Link>
                </Button>
              </div>

              {/* Quick direct contact & trust points */}
              <div className="w-full border-t border-border pt-8 mt-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
                  {trustHighlights.map((item, i) => {
                  const IconComponent = item.icon;
                  return <div key={item.id} data-index={i} className="flex items-start gap-3 rounded-lg bg-secondary/40 p-3.5 border border-border/50">
                        <div className="rounded-md bg-secondary p-2 text-chart-1 shrink-0">
                          <IconComponent className="size-4.5" />
                        </div>
                        <div>
                          <p className="font-sans text-sm font-semibold text-foreground">
                            {item.title}
                          </p>
                          <p className="font-sans text-xs text-muted-foreground">
                            {item.subtitle}
                          </p>
                        </div>
                      </div>;
                })}
                </div>

                {/* Studio Direct Phone Line */}
                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground font-sans">
                  <span>Prefer to talk directly?</span>
                  <a href="tel:+14708479076" className="inline-flex items-center gap-1.5 font-semibold text-foreground hover:text-chart-1 transition-colors">
                    <Phone className="size-3.5 text-chart-1" />
                    <span>(470) 847-9076</span>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}